import { CLIENT_ERROR_LIMITS, CLIENT_ERRORS_PER_SESSION, scrubReportText, type ClientErrorReport, type ClientErrorSource } from '@calling/shared';
import { NetworkError } from './api';
import { API_BASE, isNative, platform } from './platform';

/**
 * Reports problems the app runs into (a page that crashed, an error nothing
 * caught) to its own server, which writes them to its log: there is no
 * outside crash reporting service. A report carries what went wrong, where,
 * and the app's version, and nothing that says who someone is: email
 * addresses, meeting codes, the keys in links and IDs are taken out first.
 */

const VERSION = import.meta.env.VITE_APP_VERSION ?? '0';
/** Problems that are not the app's: the browser's own noise, and requests that never reached the server (which could not report them either). */
const IGNORED = /ResizeObserver loop|^Script error\.?$/;

let sent = 0;
const seen = new Set<string>();
/** The native app's build number, once it is known. */
let build: string | undefined;

function describe(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) return { message: `${error.name}: ${error.message}`, stack: error.stack };
  if (typeof error === 'string') return { message: error };
  try {
    return { message: JSON.stringify(error) ?? String(error) };
  } catch {
    return { message: String(error) };
  }
}

/** What is sent for a problem, or null when there is nothing to send. Exported for the tests. */
export function errorReport(where: ClientErrorSource, error: unknown, componentStack?: string): ClientErrorReport | null {
  if (error instanceof NetworkError) return null;
  const { message, stack } = describe(error);
  if (!message || IGNORED.test(message)) return null;
  const trace = [stack, componentStack].filter(Boolean).join('\n');
  return {
    message: scrubReportText(message).slice(0, CLIENT_ERROR_LIMITS.message),
    ...(trace ? { stack: scrubReportText(trace).slice(0, CLIENT_ERROR_LIMITS.stack) } : {}),
    where,
    route: scrubReportText(window.location.pathname).slice(0, CLIENT_ERROR_LIMITS.route),
    platform,
    version: VERSION.slice(0, CLIENT_ERROR_LIMITS.version),
    ...(build ? { build: build.slice(0, CLIENT_ERROR_LIMITS.build) } : {}),
  };
}

/** Sends a report, each problem once, and at most a few in all from one session. */
export function reportError(where: ClientErrorSource, error: unknown, componentStack?: string): void {
  if (sent >= CLIENT_ERRORS_PER_SESSION) return;
  const report = errorReport(where, error, componentStack);
  if (!report) return;
  const key = `${report.where}\n${report.message}\n${report.route}`;
  if (seen.has(key)) return;
  seen.add(key);
  sent++;
  // keepalive: a report sent just before the page reloads still arrives.
  void fetch(`${API_BASE}/api/client-errors`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-requested-with': 'calling' },
    body: JSON.stringify(report),
    credentials: 'omit',
    keepalive: true,
  }).catch(() => {});
}

/** Listens, for the life of the app, for errors that nothing else caught. */
export function startErrorReports(): void {
  window.addEventListener('error', (e) => reportError('window', e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => reportError('promise', e.reason));
  if (isNative) {
    void import('@capacitor/app')
      .then(({ App }) => App.getInfo())
      .then((info) => (build = info.build))
      .catch(() => {});
  }
}
